import { ChainId } from '@uniswap/sdk-core'
import bnbCircleLogoUrl from 'assets/images/bnbCircle.svg'
import ethereumLogoUrl from 'assets/images/ethereum-logo.png'
import polygonCircleLogoUrl from 'assets/images/polygonCircle.png'
import { default as arbitrumCircleLogoUrl, default as arbitrumLogoUrl } from 'assets/svg/arbitrum_logo.svg'
import avaxLogo from 'assets/svg/avax_logo.svg'
import avaxSquareLogo from 'assets/svg/avax_square_logo.svg'
import dbkLogo from 'assets/images/dbk-logo.png'
import dbkSquareLogo from 'assets/images/dbk-square-logo.png'
import berachainLogo from 'assets/svg/berachain_logo.svg'
import berachainSquareLogo from 'assets/svg/berachain_square_logo.svg'
import hyperliquidLogo from 'assets/images/hyperliquid-logo.png'
import hyperliquidSquareLogo from 'assets/images/hyperliquid-square-logo.png'
import sonicLogo from 'assets/images/sonic-logo.png'
import sonicSquareLogo from 'assets/images/sonic-square-logo.png'
import katanaLogo from 'assets/images/katana-logo.png'
import katanaSquareLogo from 'assets/images/katana-square-logo.png'
import mitosisLogo from 'assets/images/mitosis-logo.png'
import mitosisSquareLogo from 'assets/images/mitosis-square-logo.png'
import somniaLogo from 'assets/images/somnia-logo.png'
import somniaSquareLogo from 'assets/images/somnia-square-logo.png'
import zerogLogo from 'assets/images/zerog-logo.png'
import zerogSquareLogo from 'assets/images/zerog-square-logo.png'
import plasmaLogo from 'assets/images/plasma-logo.png'
import plasmaSquareLogo from 'assets/images/plasma-square-logo.png'
import monadTestnetLogo from 'assets/images/monad-testnet-logo.png'
import monadTestnetSquareLogo from 'assets/images/monad-testnet-square-logo.png'
import monadLogo from 'assets/images/monad-logo.png'
import monadSquareLogo from 'assets/images/monad-square-logo.png'
import megaethLogo from 'assets/images/megaeth-logo.png'
import megaethSquareLogo from 'assets/images/megaeth-square-logo.png'
import stableLogo from 'assets/images/stable-logo.png'
import stableSquareLogo from 'assets/images/stable-square-logo.png'
import baseLogo from 'assets/svg/base_logo.svg'
import baseSquareLogo from 'assets/svg/base_square_logo.svg'
import bnbSquareLogoUrl from 'assets/svg/bnb_square_logo.svg'
import bnbLogo from 'assets/svg/bnb-logo.svg'
import celoLogo from 'assets/svg/celo_logo.svg'
import celoSquareLogoUrl from 'assets/svg/celo_square_logo.svg'
import optimismSquareLogoUrl from 'assets/svg/optimism_square_logo.svg'
import optimismLogoUrl from 'assets/svg/optimistic_ethereum.svg'
import polygonSquareLogoUrl from 'assets/svg/polygon_square_logo.svg'
import polygonMaticLogo from 'assets/svg/polygon-matic-logo.svg'
import ms from 'ms'
import { darkTheme } from 'theme/colors'

import { SupportedL1ChainId, SupportedL2ChainId } from './chains'
import { ARBITRUM_LIST, AVALANCHE_LIST, BASE_LIST, CELO_LIST, NOXA_LIST, OPTIMISM_LIST, PLASMA_BNB_LIST } from './lists'

export const AVERAGE_L1_BLOCK_TIME = ms(`12s`)

export enum NetworkType {
  L1,
  L2,
}
interface BaseChainInfo {
  readonly networkType: NetworkType
  readonly blockWaitMsBeforeWarning?: number
  readonly docs: string
  readonly bridge?: string
  readonly explorer: string
  readonly infoLink: string
  readonly logoUrl: string
  readonly circleLogoUrl?: string
  readonly squareLogoUrl?: string
  readonly label: string
  readonly helpCenterUrl?: string
  readonly nativeCurrency: {
    name: string // e.g. 'Goerli ETH',
    symbol: string // e.g. 'gorETH',
    decimals: number // e.g. 18,
  }
  readonly color?: string
  readonly backgroundColor?: string
}

interface L1ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L1
  readonly defaultListUrl?: string
}

export interface L2ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L2
  readonly bridge: string
  readonly statusPage?: string
  readonly defaultListUrl: string
}

type ChainInfoMap = { readonly [chainId: number]: L1ChainInfo | L2ChainInfo } & {
  readonly [chainId in SupportedL2ChainId]: L2ChainInfo
} & { readonly [chainId in SupportedL1ChainId]: L1ChainInfo }

const CHAIN_INFO: ChainInfoMap = {
  [ChainId.MAINNET]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Ethereum',
    logoUrl: ethereumLogoUrl,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_1,
  },
  [ChainId.GOERLI]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://goerli.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Görli',
    logoUrl: ethereumLogoUrl,
    nativeCurrency: { name: 'Görli Ether', symbol: 'görETH', decimals: 18 },
    color: darkTheme.chain_5,
  },
  [ChainId.SEPOLIA]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://sepolia.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Sepolia',
    logoUrl: ethereumLogoUrl,
    nativeCurrency: { name: 'Sepolia Ether', symbol: 'SepoliaETH', decimals: 18 },
    color: darkTheme.chain_5,
  },
  [ChainId.OPTIMISM]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://app.optimism.io/bridge',
    defaultListUrl: OPTIMISM_LIST,
    docs: 'https://optimism.io/',
    explorer: 'https://optimistic.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/optimism/',
    label: 'Optimism',
    logoUrl: optimismLogoUrl,
    // Optimism perfers same icon for both
    circleLogoUrl: optimismLogoUrl,
    squareLogoUrl: optimismSquareLogoUrl,
    statusPage: 'https://optimism.io/status',
    helpCenterUrl: 'https://help.uniswap.org/en/collections/3137778-uniswap-on-optimistic-ethereum-oξ',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_10,
    backgroundColor: darkTheme.chain_10_background,
  },
  [ChainId.OPTIMISM_GOERLI]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://app.optimism.io/bridge',
    defaultListUrl: OPTIMISM_LIST,
    docs: 'https://optimism.io/',
    explorer: 'https://goerli-optimism.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/optimism/',
    label: 'Optimism Görli',
    logoUrl: optimismLogoUrl,
    statusPage: 'https://optimism.io/status',
    helpCenterUrl: 'https://help.uniswap.org/en/collections/3137778-uniswap-on-optimistic-ethereum-oξ',
    nativeCurrency: { name: 'Optimism Goerli Ether', symbol: 'görOpETH', decimals: 18 },
    color: darkTheme.chain_420,
  },
  [ChainId.ARBITRUM_ONE]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://bridge.arbitrum.io/',
    docs: 'https://offchainlabs.com/',
    explorer: 'https://arbiscan.io/',
    infoLink: 'https://info.uniswap.org/#/arbitrum',
    label: 'Arbitrum',
    logoUrl: arbitrumLogoUrl,
    circleLogoUrl: arbitrumCircleLogoUrl,
    defaultListUrl: ARBITRUM_LIST,
    helpCenterUrl: 'https://help.uniswap.org/en/collections/3137787-uniswap-on-arbitrum',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_42,
    backgroundColor: darkTheme.chain_42161_background,
  },
  [ChainId.ARBITRUM_GOERLI]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://bridge.arbitrum.io/',
    docs: 'https://offchainlabs.com/',
    explorer: 'https://goerli.arbiscan.io/',
    infoLink: 'https://info.uniswap.org/#/arbitrum/',
    label: 'Arbitrum Goerli',
    logoUrl: arbitrumLogoUrl,
    defaultListUrl: ARBITRUM_LIST, // TODO: use arbitrum goerli token list
    helpCenterUrl: 'https://help.uniswap.org/en/collections/3137787-uniswap-on-arbitrum',
    nativeCurrency: { name: 'Goerli Arbitrum Ether', symbol: 'goerliArbETH', decimals: 18 },
    color: darkTheme.chain_421613,
  },
  [ChainId.POLYGON]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://wallet.polygon.technology/polygon/bridge',
    docs: 'https://polygon.io/',
    explorer: 'https://polygonscan.com/',
    infoLink: 'https://info.uniswap.org/#/polygon/',
    label: 'Polygon',
    logoUrl: polygonMaticLogo,
    circleLogoUrl: polygonCircleLogoUrl,
    squareLogoUrl: polygonSquareLogoUrl,
    nativeCurrency: { name: 'Polygon Matic', symbol: 'MATIC', decimals: 18 },
    color: darkTheme.chain_137,
    backgroundColor: darkTheme.chain_137_background,
  },
  [ChainId.POLYGON_MUMBAI]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://wallet.polygon.technology/polygon/bridge/deposit',
    docs: 'https://polygon.io/',
    explorer: 'https://mumbai.polygonscan.com/',
    infoLink: 'https://info.uniswap.org/#/polygon/',
    label: 'Polygon Mumbai',
    logoUrl: polygonMaticLogo,
    nativeCurrency: { name: 'Polygon Mumbai Matic', symbol: 'mMATIC', decimals: 18 },
  },
  [ChainId.CELO]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://www.portalbridge.com/#/transfer',
    docs: 'https://docs.celo.org/',
    explorer: 'https://celoscan.io/',
    infoLink: 'https://info.uniswap.org/#/celo/',
    label: 'Celo',
    logoUrl: celoLogo,
    circleLogoUrl: celoLogo,
    squareLogoUrl: celoSquareLogoUrl,
    nativeCurrency: { name: 'Celo', symbol: 'CELO', decimals: 18 },
    defaultListUrl: CELO_LIST,
  },
  [ChainId.CELO_ALFAJORES]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://www.portalbridge.com/#/transfer',
    docs: 'https://docs.celo.org/',
    explorer: 'https://alfajores-blockscout.celo-testnet.org/',
    infoLink: 'https://info.uniswap.org/#/celo/',
    label: 'Celo Alfajores',
    logoUrl: celoLogo,
    nativeCurrency: { name: 'Celo', symbol: 'CELO', decimals: 18 },
    defaultListUrl: CELO_LIST,
  },
  [ChainId.BNB]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://cbridge.celer.network/1/56',
    docs: 'https://docs.bnbchain.org/',
    explorer: 'https://bscscan.com/',
    infoLink: 'https://info.uniswap.org/#/bnb/',
    label: 'BNB Chain',
    logoUrl: bnbLogo,
    circleLogoUrl: bnbCircleLogoUrl,
    squareLogoUrl: bnbSquareLogoUrl,
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    defaultListUrl: PLASMA_BNB_LIST,
    color: darkTheme.chain_56,
    backgroundColor: darkTheme.chain_56_background,
  },
  [ChainId.AVALANCHE]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`10m`),
    bridge: 'https://core.app/bridge/',
    docs: 'https://docs.avax.network/',
    explorer: 'https://snowtrace.io/',
    infoLink: 'https://info.uniswap.org/#/avax/', // TODO(WEB-2336): Add avax support to info site
    label: 'Avalanche',
    logoUrl: avaxLogo,
    circleLogoUrl: avaxLogo,
    squareLogoUrl: avaxSquareLogo,
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    defaultListUrl: AVALANCHE_LIST,
    color: darkTheme.chain_43114,
    backgroundColor: darkTheme.chain_43114_background,
  },
  [ChainId.BASE]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://bridge.base.org/deposit',
    defaultListUrl: BASE_LIST,
    docs: 'https://docs.base.org',
    explorer: 'https://basescan.org/',
    infoLink: 'https://info.uniswap.org/#/base/',
    label: 'Base',
    logoUrl: baseLogo,
    statusPage: 'https://status.base.org/',
    circleLogoUrl: baseLogo,
    squareLogoUrl: baseSquareLogo,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_84531,
  },
  [ChainId.DBK]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://bridge.base.org/deposit',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.dbkchain.io',
    explorer: 'https://scan.dbkchain.io/',
    infoLink: 'https://info.uniswap.org/#/base/',
    label: 'DBK',
    logoUrl: dbkLogo,
    statusPage: 'https://status.dbkchain.io/',
    circleLogoUrl: dbkLogo,
    squareLogoUrl: dbkSquareLogo,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_84531,
  },
  [ChainId.BERACHAIN]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://bridge.berachain.com',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.berachain.com',
    explorer: 'https://berascan.com',
    infoLink: 'https://info.noxa.fi/#/berachain/',
    label: 'Berachain',
    logoUrl: berachainLogo,
    circleLogoUrl: berachainLogo,
    squareLogoUrl: berachainSquareLogo,
    nativeCurrency: { name: 'Bera', symbol: 'BERA', decimals: 18 },
    color: darkTheme.chain_80094,
  },
  [ChainId.HYPERLIQUID]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://bridge.hyperliquid.xyz',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.hyperliquid.xyz',
    explorer: 'https://hyperliquid.cloud.blockscout.com',
    infoLink: 'https://info.noxa.fi/#/hyperliquid/',
    label: 'Hyperliquid',
    logoUrl: hyperliquidLogo,
    circleLogoUrl: hyperliquidLogo,
    squareLogoUrl: hyperliquidSquareLogo,
    nativeCurrency: { name: 'Hype', symbol: 'HYPE', decimals: 18 },
    color: darkTheme.chain_999,
  },
  [ChainId.SONIC]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://gateway.soniclabs.com/ethereum/sonic/ftm-s',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.soniclabs.com',
    explorer: 'https://sonicscan.org',
    infoLink: 'https://info.noxa.fi/#/sonic/',
    label: 'Sonic',
    logoUrl: sonicLogo,
    circleLogoUrl: sonicLogo,
    squareLogoUrl: sonicSquareLogo,
    nativeCurrency: { name: 'Sonic', symbol: 'S', decimals: 18 },
    color: darkTheme.chain_146,
  },
  [ChainId.KATANA]: {
    networkType: NetworkType.L2,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://gas.zip',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.katana.network',
    explorer: 'https://explorer.katanarpc.com',
    infoLink: 'https://info.noxa.fi/#/katana/',
    label: 'Katana',
    logoUrl: katanaLogo,
    circleLogoUrl: katanaLogo,
    squareLogoUrl: katanaSquareLogo,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_146,
  },
  [ChainId.MITOSIS]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.mitosis.org',
    explorer: 'https://blockscout.mtss-main.cytokinesis.me',
    infoLink: 'https://info.noxa.fi/#/mitosis/',
    label: 'Mitosis',
    logoUrl: mitosisLogo,
    circleLogoUrl: mitosisLogo,
    squareLogoUrl: mitosisSquareLogo,
    nativeCurrency: { name: 'Mitosis', symbol: 'MITO', decimals: 18 },
    color: darkTheme.chain_124816,
  },
  [ChainId.SOMNIA]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.somnia.network',
    explorer: 'https://explorer.somnia.network',
    infoLink: 'https://info.noxa.fi/#/somnia/',
    label: 'Somnia',
    logoUrl: somniaLogo,
    circleLogoUrl: somniaLogo,
    squareLogoUrl: somniaSquareLogo,
    nativeCurrency: { name: 'Somnia', symbol: 'SOMI', decimals: 18 },
    color: darkTheme.chain_5031,
  },
  [ChainId.ZEROG]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.0g.ai',
    explorer: 'https://chainscan.0g.ai',
    infoLink: 'https://info.noxa.fi/#/zerog/',
    label: '0G',
    logoUrl: zerogLogo,
    circleLogoUrl: zerogLogo,
    squareLogoUrl: zerogSquareLogo,
    nativeCurrency: { name: '0G', symbol: '0G', decimals: 18 },
    color: darkTheme.chain_16661,
  },
  [ChainId.PLASMA]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://plasma.to',
    explorer: 'https://plasmascan.to',
    infoLink: 'https://info.noxa.fi/#/plasma/',
    label: 'Plasma',
    logoUrl: plasmaLogo,
    circleLogoUrl: plasmaLogo,
    squareLogoUrl: plasmaSquareLogo,
    nativeCurrency: { name: 'Plasma', symbol: 'XPL', decimals: 18 },
    color: darkTheme.chain_9745,
  },
  [ChainId.MONAD_TESTNET]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://bridge.monad.xyz',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.monad.xyz',
    explorer: 'https://testnet.monadexplorer.com',
    infoLink: 'https://info.noxa.fi/#/monad/',
    label: 'Monad Testnet',
    logoUrl: monadTestnetLogo,
    circleLogoUrl: monadTestnetLogo,
    squareLogoUrl: monadTestnetSquareLogo,
    nativeCurrency: { name: 'Testnet MON Token', symbol: 'MON', decimals: 18 },
    color: darkTheme.chain_10143,
  },
  [ChainId.MONAD]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    bridge: 'https://monadbridge.com',
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.monad.xyz',
    explorer: 'https://explorer.monad.xyz',
    infoLink: 'https://info.noxa.fi/#/monad/',
    label: 'Monad',
    logoUrl: monadLogo,
    circleLogoUrl: monadLogo,
    squareLogoUrl: monadSquareLogo,
    nativeCurrency: { name: 'Monad', symbol: 'MON', decimals: 18 },
    color: darkTheme.chain_143,
  },
  [ChainId.MEGAETH]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.megaeth.com',
    explorer: 'https://megaeth-private.blockscout.com',
    infoLink: 'https://info.noxa.fi/#/megaeth/',
    label: 'MegaETH',
    logoUrl: megaethLogo,
    circleLogoUrl: megaethLogo,
    squareLogoUrl: megaethSquareLogo,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    color: darkTheme.chain_4326,
  },
  [ChainId.STABLE]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms(`25m`),
    defaultListUrl: NOXA_LIST,
    docs: 'https://docs.stable.xyz',
    explorer: 'https://stablescan.xyz',
    infoLink: 'https://info.noxa.fi/#/stable/',
    label: 'Stable',
    logoUrl: stableLogo,
    circleLogoUrl: stableLogo,
    squareLogoUrl: stableSquareLogo,
    nativeCurrency: { name: 'gUSDT', symbol: 'gUSDT', decimals: 18 },
    color: darkTheme.chain_988,
  },
} as const

export function getChainInfo(chainId: SupportedL1ChainId): L1ChainInfo
export function getChainInfo(chainId: SupportedL2ChainId): L2ChainInfo
export function getChainInfo(chainId: ChainId): L1ChainInfo | L2ChainInfo
export function getChainInfo(
  chainId: ChainId | SupportedL1ChainId | SupportedL2ChainId | number | undefined
): L1ChainInfo | L2ChainInfo | undefined

/**
 * Overloaded method for returning ChainInfo given a chainID
 * Return type varies depending on input type:
 * number | undefined -> returns chaininfo | undefined
 * ChainId -> returns L1ChainInfo | L2ChainInfo
 * SupportedL1ChainId -> returns L1ChainInfo
 * SupportedL2ChainId -> returns L2ChainInfo
 */
export function getChainInfo(chainId: any): any {
  if (chainId) {
    return CHAIN_INFO[chainId] ?? undefined
  }
  return undefined
}

const MAINNET_INFO = CHAIN_INFO[ChainId.MONAD]
export function getChainInfoOrDefault(chainId: number | undefined) {
  return getChainInfo(chainId) ?? MAINNET_INFO
}
